import { useQuery } from "@tanstack/react-query";
import { PageHeader, StatBadge, Section } from "@/components/Chrome";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";

export default function Dashboard() {
  const { data: profile } = useQuery({ queryKey: ["profile"], queryFn: api.profile.get });
  const { data: activity = [] } = useQuery({ queryKey: ["activity"], queryFn: () => api.activity.list(12), refetchInterval: 5000 });
  const { data: approvals = [] } = useQuery({ queryKey: ["approvals", "pending"], queryFn: () => api.approvals.list("pending"), refetchInterval: 5000 });
  const { data: integrations = [] } = useQuery({ queryKey: ["integrations"], queryFn: api.integrations.list });
  const { data: memory = [] } = useQuery({ queryKey: ["memory"], queryFn: api.memory.list });

  const connectedCount = integrations.filter(i => i.status === "connected").length;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Command Center"
        title={`Good morning, ${profile?.name || "Founder"}.`}
        subtitle="Copilot runs quietly in the background — reading your inbox, holding your calendar, and asking you only when it needs a decision. Everything below is a live view of what it did overnight."
      >
        <Link to="/conversation" data-testid="btn-open-conversation"
          className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-mono bg-orange-600 hover:bg-orange-500 text-white transition-colors duration-150">
          Open conversation <ArrowRight size={14} weight="bold" />
        </Link>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-4 border-b border-stone-800">
        <Stat label="Pending approvals" value={approvals.length} accent={approvals.length > 0 ? "accent" : "info"} testid="stat-approvals" />
        <Stat label="Connected accounts" value={`${connectedCount} / ${integrations.length}`} testid="stat-integrations" />
        <Stat label="Memory items" value={memory.length} testid="stat-memory" />
        <Stat label="Actions today" value={activity.length} testid="stat-actions" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3">
        <div className="lg:col-span-2 border-r border-stone-800">
          <Section title="Activity stream">
            {activity.length === 0 ? (
              <EmptyState text="No activity yet. Try starting a conversation with Copilot." />
            ) : (
              <ul className="divide-y divide-stone-800/70" data-testid="activity-stream">
                {activity.map((a) => (
                  <li key={a.id} className="py-3 flex items-start gap-4">
                    <span className="font-mono text-[11px] text-stone-500 w-32 shrink-0">{new Date(a.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    <StatBadge status={a.status === "success" ? "success" : a.status === "warning" ? "warning" : a.status === "danger" ? "danger" : "info"}>{a.kind}</StatBadge>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-stone-200">{a.summary}</div>
                      {a.detail && <div className="text-xs text-stone-500 mt-1 truncate font-mono">{a.detail}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <div>
          <Section title="Pending approvals" right={<Link to="/approvals" className="text-[10px] uppercase tracking-widest text-stone-400 hover:text-orange-500">All →</Link>}>
            {approvals.length === 0 ? (
              <EmptyState text="Nothing waiting on you." />
            ) : (
              <ul className="space-y-3" data-testid="pending-approvals-list">
                {approvals.slice(0, 5).map(a => (
                  <li key={a.id} className="border border-stone-800 p-3">
                    <div className="text-xs font-mono uppercase tracking-widest text-orange-500">{a.action}</div>
                    <div className="text-sm mt-1 text-stone-200">{a.label}</div>
                    <div className="text-xs text-stone-500 mt-1 line-clamp-2">{a.payload?.instruction}</div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Memory highlights" right={<Link to="/memory" className="text-[10px] uppercase tracking-widest text-stone-400 hover:text-orange-500">All →</Link>}>
            {memory.length === 0 ? (
              <EmptyState text={`Try saying: "remember: I always CC my co-founder on investor emails"`} />
            ) : (
              <ul className="space-y-2 font-mono text-xs" data-testid="memory-highlights">
                {memory.slice(0, 5).map(m => (
                  <li key={m.id} className="text-stone-300">
                    <span className="text-stone-500">{m.kind}·</span> {m.key}: <span className="text-stone-100">{m.value}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "info", testid }) {
  const color = accent === "accent" ? "text-orange-500" : "text-stone-100";
  return (
    <div className="px-8 py-8 border-r border-stone-800 last:border-r-0" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-stone-500">{label}</div>
      <div className={`font-serif text-4xl mt-3 ${color}`}>{value}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="text-sm text-stone-500 py-4">{text}</div>;
}
